import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Minus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, apiError, queueOfflineDevis } from "@/lib/api";
import PhotoCapture from "@/components/PhotoCapture";

export default function DevisNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [clientId, setClientId] = useState("");
  const [chantierId, setChantierId] = useState("");
  const [lignes, setLignes] = useState([]);
  const [objet, setObjet] = useState("");
  const [modeMo, setModeMo] = useState("forfait");
  const [heuresMo, setHeuresMo] = useState("");
  const [tauxMo, setTauxMo] = useState("");
  const [tvaMo, setTvaMo] = useState("20");
  const [montantForfaitMo, setMontantForfaitMo] = useState("");
  const [newClient, setNewClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ nom: "", telephone: "" });
  const [newChantier, setNewChantier] = useState(false);
  const [newChantierTitre, setNewChantierTitre] = useState("");
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [numero, setNumero] = useState("");
  const [prefillChantier, setPrefillChantier] = useState(undefined);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/devis/${id}`).then((r) => {
      const d = r.data;
      if (d.statut !== "brouillon") {
        toast.error(t("devis_form.brouillon_seul"));
        navigate(`/devis/${id}`);
        return;
      }
      setNumero(d.numero);
      setPrefillChantier(d.chantier_id || "");
      setClientId(d.client_id);
      setLignes(d.lignes.map((l) => ({
        designation: l.designation, quantite: l.quantite,
        prix_unitaire: l.prix_unitaire, article_stock_id: l.article_stock_id || null,
        taux_tva: l.autoliquidation ? 0 : (l.taux_tva ?? 20), autoliquidation: !!l.autoliquidation,
        unite: l.unite || "",
      })));
      setObjet(d.objet || "");
      setModeMo(d.mode_mo === "horaire" ? "horaire" : "forfait");
      setHeuresMo(d.heures_mo || "");
      setTauxMo(d.taux_horaire_mo || "");
      setTvaMo(d.autoliquidation_mo ? "AL" : String(d.taux_tva_mo ?? 20));
      setMontantForfaitMo(d.montant_forfait_mo || "");
    }).catch(() => navigate("/devis"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/articles").then((r) => setArticles(r.data));
  }, []);

  useEffect(() => {
    if (clientId) {
      api.get(`/chantiers?client_id=${clientId}`).then((r) => setChantiers(r.data));
      if (prefillChantier !== undefined) {
        setChantierId(prefillChantier);
        setPrefillChantier(undefined);
      } else {
        setChantierId("");
      }
    } else {
      setChantiers([]);
    }
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const parTaux = {};
    let al = false;
    for (const l of lignes) {
      const base = (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0);
      const taux = l.autoliquidation ? 0 : Number(l.taux_tva ?? 20);
      const clef = l.autoliquidation ? "AL" : taux;
      if (!parTaux[clef]) parTaux[clef] = { taux, autoliquidation: !!l.autoliquidation, base_ht: 0, tva: 0 };
      parTaux[clef].base_ht += base;
      if (l.autoliquidation) al = true;
    }
    const baseMo = modeMo === "horaire"
      ? (Number(heuresMo) || 0) * (Number(tauxMo) || 0)
      : (Number(montantForfaitMo) || 0);
    if (baseMo > 0) {
      const alMo = tvaMo === "AL";
      const tauxMoNum = alMo ? 0 : Number(tvaMo);
      const clefMo = alMo ? "AL" : tauxMoNum;
      if (!parTaux[clefMo]) parTaux[clefMo] = { taux: tauxMoNum, autoliquidation: alMo, base_ht: 0, tva: 0 };
      parTaux[clefMo].base_ht += baseMo;
      if (alMo) al = true;
    }
    const ventilation = Object.values(parTaux)
      .sort((a, b) => Number(a.autoliquidation) - Number(b.autoliquidation) || b.taux - a.taux);
    for (const v of ventilation) v.tva = v.autoliquidation ? 0 : v.base_ht * v.taux / 100;
    const ht = ventilation.reduce((s, v) => s + v.base_ht, 0);
    const totalTva = ventilation.reduce((s, v) => s + v.tva, 0);
    return { ht, tva: totalTva, ttc: ht + totalTva, ventilation, autoliquidation: al };
  }, [lignes, modeMo, heuresMo, tauxMo, tvaMo, montantForfaitMo]);

  const setLigne = (i, k, v) =>
    setLignes(lignes.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  async function createClientRapide() {
    if (!newClientForm.nom.trim()) return;
    try {
      const { data } = await api.post("/clients", newClientForm);
      setClients([data, ...clients]);
      setClientId(data.id);
      setNewClient(false);
      toast.success(t("devis_form.client_cree", { nom: data.nom }));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function createChantierRapide() {
    if (!newChantierTitre.trim()) return;
    try {
      const { data } = await api.post("/chantiers", { client_id: clientId, titre: newChantierTitre });
      setChantiers([data, ...chantiers]);
      setChantierId(data.id);
      setNewChantier(false);
      toast.success(t("devis_form.chantier_cree"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function submit() {
    if (!clientId) return toast.error(t("devis_form.client_requis"));
    if (lignes.length === 0) return toast.error(t("devis_form.ligne_requise"));
    setSaving(true);
    const payload = {
      client_id: clientId,
      chantier_id: chantierId || null,
      objet,
      mode_mo: modeMo,
      heures_mo: modeMo === "horaire" ? (Number(heuresMo) || 0) : 0,
      taux_horaire_mo: modeMo === "horaire" ? (Number(tauxMo) || 0) : 0,
      taux_tva_mo: tvaMo === "AL" ? 0 : Number(tvaMo),
      autoliquidation_mo: tvaMo === "AL",
      montant_forfait_mo: modeMo === "forfait" ? (Number(montantForfaitMo) || 0) : 0,
      lignes: lignes.map((l) => ({
        designation: l.designation,
        quantite: Number(l.quantite) || 1,
        prix_unitaire: Number(l.prix_unitaire) || 0,
        article_stock_id: l.article_stock_id || null,
        taux_tva: l.autoliquidation ? 0 : Number(l.taux_tva ?? 20),
        autoliquidation: !!l.autoliquidation,
        unite: l.unite || "",
      })),
    };
    try {
      if (isEdit) {
        await api.put(`/devis/${id}`, payload);
        toast.success(t("devis_form.devis_modifie", { numero }));
        navigate(`/devis/${id}`);
        return;
      }
      const { data } = await api.post("/devis", payload);
      toast.success(t("devis_form.devis_cree", { numero: data.numero }));
      navigate(`/devis/${data.id}`);
    } catch (e) {
      if (!e?.response && !isEdit) {
        queueOfflineDevis(payload);
        toast.success(t("devis_form.hors_ligne"));
        navigate("/devis");
        return;
      }
      toast.error(apiError(e));
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-nouveau-devis">
      <h1 className="font-serif font-bold text-2xl sm:text-3xl">
        {isEdit ? t("devis_form.titre_modifier", { numero }) : t("devis_form.titre_nouveau")}
      </h1>

      <div className="panel space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium">{t("devis_form.client")}</label>
            <button onClick={() => setNewClient(!newClient)} className="text-[#8A5A2B] text-sm font-semibold"
                    data-testid="btn-nouveau-client-rapide">
              {t("devis_form.nouveau_client")}
            </button>
          </div>
          {newClient ? (
            <div className="flex gap-2">
              <input className="input-field" placeholder={t("devis_form.nom_client")} value={newClientForm.nom}
                     onChange={(e) => setNewClientForm({ ...newClientForm, nom: e.target.value })}
                     data-testid="input-nouveau-client-nom" />
              <input className="input-field" placeholder={t("devis_form.telephone")} value={newClientForm.telephone}
                     onChange={(e) => setNewClientForm({ ...newClientForm, telephone: e.target.value })}
                     data-testid="input-nouveau-client-tel" />
              <button onClick={createClientRapide} className="btn-primary !px-4 shrink-0" data-testid="btn-creer-client-rapide">
                {t("commun.creer")}
              </button>
            </div>
          ) : (
            <select className="input-field" value={clientId} onChange={(e) => setClientId(e.target.value)}
                    data-testid="select-client">
              <option value="">{t("devis_form.choisir_client")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          )}
        </div>

        {clientId && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">{t("devis_form.chantier")}</label>
              <button onClick={() => setNewChantier(!newChantier)} className="text-[#8A5A2B] text-sm font-semibold"
                      data-testid="btn-nouveau-chantier-rapide">
                {t("devis_form.nouveau_chantier")}
              </button>
            </div>
            {newChantier ? (
              <div className="flex gap-2">
                <input className="input-field" placeholder={t("devis_form.chantier_ex")} value={newChantierTitre}
                       onChange={(e) => setNewChantierTitre(e.target.value)} data-testid="input-nouveau-chantier-titre" />
                <button onClick={createChantierRapide} className="btn-primary !px-4 shrink-0" data-testid="btn-creer-chantier-rapide">
                  {t("commun.creer")}
                </button>
              </div>
            ) : (
              <select className="input-field" value={chantierId} onChange={(e) => setChantierId(e.target.value)}
                      data-testid="select-chantier">
                <option value="">{t("devis_form.aucun_chantier")}</option>
                {chantiers.map((c) => (
                  <option key={c.id} value={c.id}>{c.titre}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-1.5 block">{t("devis_form.objet")}</label>
          <input className="input-field" placeholder={t("devis_form.objet_ph")} value={objet}
                 onChange={(e) => setObjet(e.target.value)} maxLength={120} data-testid="input-objet-devis" />
        </div>
      </div>

      <div className="panel space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-serif font-semibold text-lg">{t("devis_form.prestations")}</h2>
          <button onClick={() => setLignes([...lignes, { designation: "", quantite: 1, prix_unitaire: "", article_stock_id: null, unite: "" }])}
                  className="btn-secondary !h-11" data-testid="btn-ajouter-ligne-devis">
            <Plus size={18} /> {t("devis_form.ajouter_ligne")}
          </button>
        </div>

        {articles.length > 0 && (
          <select className="input-field" value="" data-testid="select-article-stock"
                  onChange={(e) => {
                    const a = articles.find((x) => x.id === e.target.value);
                    if (a) setLignes([...lignes, { designation: a.nom, quantite: 1, prix_unitaire: "", article_stock_id: a.id, unite: a.unite || "" }]);
                  }}>
            <option value="">{t("devis_form.ajouter_stock")}</option>
            {articles.map((a) => (
              <option key={a.id} value={a.id}>{a.nom} ({a.quantite}{a.unite ? ` ${a.unite}` : ""} {t("devis_form.dispo")})</option>
            ))}
          </select>
        )}

        {lignes.length === 0 && (
          <p className="text-stone-500 text-sm py-4 text-center">{t("devis_form.lignes_vide")}</p>
        )}

        {lignes.map((l, i) => (
          <div key={i} className="border border-[#E6DFD1] rounded-lg p-3 space-y-2 bg-[#FDFBF7]" data-testid={`ligne-devis-${i}`}>
            <div className="flex gap-2">
              <input className="input-field" placeholder={t("devis_form.designation")} value={l.designation}
                     onChange={(e) => setLigne(i, "designation", e.target.value)} data-testid={`ligne-designation-${i}`} />
              <button onClick={() => setLignes(lignes.filter((_, j) => j !== i))}
                      className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-[#FBE4E1] text-[#A32E20]"
                      data-testid={`ligne-supprimer-${i}`}>
                <Trash2 size={18} />
              </button>
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex items-center border border-[#E6DFD1] rounded-lg bg-white overflow-hidden">
                <button onClick={() => setLigne(i, "quantite", Math.max(0, (Number(l.quantite) || 0) - 1))}
                        className="w-11 h-12 flex items-center justify-center" data-testid={`ligne-moins-${i}`}>
                  <Minus size={16} />
                </button>
                <input className="w-14 h-12 text-center outline-none" value={l.quantite}
                       onChange={(e) => setLigne(i, "quantite", e.target.value)} data-testid={`ligne-quantite-${i}`} />
                <button onClick={() => setLigne(i, "quantite", (Number(l.quantite) || 0) + 1)}
                        className="w-11 h-12 flex items-center justify-center" data-testid={`ligne-plus-${i}`}>
                  <Plus size={16} />
                </button>
              </div>
              {l.unite && <span className="text-xs text-stone-500 shrink-0" data-testid={`ligne-unite-${i}`}>{l.unite}</span>}
              <input className="input-field" type="number" min="0" step="0.01" placeholder={t("devis_form.prix")}
                     value={l.prix_unitaire} onChange={(e) => setLigne(i, "prix_unitaire", e.target.value)}
                     data-testid={`ligne-prix-${i}`} />
              <span className="font-serif font-semibold w-24 text-right shrink-0">
                {eur((Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0))}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-stone-500 shrink-0">{t("devis_form.tva")}</label>
              <select className="input-field !h-10 !py-0 text-sm" data-testid={`ligne-tva-${i}`}
                      value={l.autoliquidation ? "AL" : String(l.taux_tva ?? 20)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLignes(lignes.map((x, j) => (j === i
                          ? (v === "AL"
                              ? { ...x, autoliquidation: true, taux_tva: 0 }
                              : { ...x, autoliquidation: false, taux_tva: Number(v) })
                          : x)));
                      }}>
                <option value="20">20 %</option>
                <option value="10">10 %</option>
                <option value="5.5">5,5 %</option>
                <option value="0">{t("devis_form.exonere")}</option>
                <option value="AL">{t("devis_form.autoliquidation")}</option>
              </select>
            </div>
          </div>
        ))}

        <p className="text-xs text-stone-500 pt-1">{t("devis_form.tva_note")}</p>
      </div>

      <div className="panel space-y-3" data-testid="bloc-main-oeuvre">
        <h2 className="font-serif font-semibold text-lg">{t("devis_form.mo_titre")}</h2>
        <div className="flex gap-1 p-1 bg-[#EFE8DC] rounded-lg w-fit" data-testid="mo-mode">
          {["forfait", "horaire"].map((m) => (
            <button key={m} type="button" onClick={() => setModeMo(m)}
                    className={`h-9 px-4 rounded-md text-sm font-semibold transition-colors ${modeMo === m ? "bg-[#8A5A2B] text-white" : "text-stone-600"}`}
                    data-testid={`mo-mode-${m}`}>
              {t(`devis_form.mode_${m}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {modeMo === "horaire" ? (
            <>
              <input className="input-field flex-1 min-w-32" type="number" min="0" step="0.5"
                     placeholder={t("devis_form.mo_heures")} value={heuresMo}
                     onChange={(e) => setHeuresMo(e.target.value)} data-testid="mo-heures" />
              <input className="input-field flex-1 min-w-32" type="number" min="0" step="0.01"
                     placeholder={t("devis_form.taux_horaire")} value={tauxMo}
                     onChange={(e) => setTauxMo(e.target.value)} data-testid="mo-taux" />
            </>
          ) : (
            <input className="input-field flex-1 min-w-32" type="number" min="0" step="0.01"
                   placeholder={t("devis_form.mo_montant")} value={montantForfaitMo}
                   onChange={(e) => setMontantForfaitMo(e.target.value)} data-testid="mo-montant" />
          )}
          <select className="input-field !h-12 flex-1 min-w-28" value={tvaMo}
                  onChange={(e) => setTvaMo(e.target.value)} data-testid="mo-tva">
            <option value="20">20 %</option>
            <option value="10">10 %</option>
            <option value="5.5">5,5 %</option>
            <option value="0">{t("devis_form.exonere")}</option>
            <option value="AL">{t("devis_form.autoliquidation")}</option>
          </select>
          <span className="font-serif font-semibold shrink-0" data-testid="mo-total">
            {eur(modeMo === "horaire"
              ? (Number(heuresMo) || 0) * (Number(tauxMo) || 0)
              : (Number(montantForfaitMo) || 0))}
          </span>
        </div>
      </div>

      <div className="bg-[#12100D] rounded-xl p-5 border border-[#D9A461]/30" data-testid="bloc-total-devis">
        <div className="flex justify-between text-stone-400 text-sm">
          <span>{t("devis_form.total_ht")}</span><span className="font-serif">{eur(totals.ht)}</span>
        </div>
        {totals.ventilation.map((v) => (
          <div key={v.autoliquidation ? "AL" : v.taux} className="flex justify-between text-stone-400 text-sm mt-1"
               data-testid={`total-tva-${v.autoliquidation ? "al" : String(v.taux).replace(".", "-")}`}>
            <span>{v.autoliquidation
              ? t("devis_form.al_zero")
              : t("devis_form.tva_ligne", { taux: String(v.taux).replace(".", ","), base: eur(v.base_ht) })}</span>
            <span className="font-serif">{eur(v.tva)}</span>
          </div>
        ))}
        {totals.autoliquidation && (
          <p className="text-[#D9A461] text-xs mt-2 font-semibold" data-testid="mention-autoliquidation">
            {t("devis_form.al_mention")}
          </p>
        )}
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
          <span className="text-white font-semibold">{t("devis_form.total_ttc")}</span>
          <span className="amount-display">{eur(totals.ttc)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PhotoCapture chantierId={chantierId} clientId={clientId} type="photo_chantier" className="btn-secondary"
                      testid="btn-photo-chantier-devis">
          {t("devis_form.photo_chantier")}
        </PhotoCapture>
        <PhotoCapture clientId={clientId} chantierId={chantierId} type="facture_fournisseur" className="btn-secondary"
                      testid="btn-photo-justificatif-devis">
          {t("devis_form.photo_justif")}
        </PhotoCapture>
      </div>
      <button onClick={submit} disabled={saving} className="btn-accent w-full" data-testid="btn-enregistrer-devis">
        {saving ? <Loader2 className="animate-spin" size={20} /> : isEdit ? t("devis_form.enregistrer_modifs") : t("devis_form.creer_devis")}
      </button>
    </div>
  );
}
